import { useCallback, useEffect, useRef, useState } from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Dropdown, Form, Menu, message, Modal, Select, Tooltip } from "antd";
import { clsx } from "clsx";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { stringify as qsStringify } from "qs";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import {
    RVIcon,
    RVIconSizeOptions,
    RVLoadingTitle,
    LoadingTitleSize,
    getURLQueryParams,
    QueryParams,
    RVFormItem,
} from "@regal-voice/ui";
import { getLogger } from "Services/LoggingService";
import { setSelectedSavedView } from "Services/state/saved-views/SavedViewsProvider";
import {
    selectDefaultSavedView,
    selectLoading,
    selectSavedViews,
    selectSelectedSavedViewId,
} from "Services/state/saved-views/Selectors";
import { deleteSavedView, loadSavedViews, saveNewView, updateSavedView } from "Services/state/saved-views/Thunks";
import { useRVDispatch } from "Services/state/Storage";
import { SavedView } from "Types/SavedViews";

import { SaveViewModal } from "./SaveViewModal/SaveViewModal";
import { areQueryParamsEqual } from "./utils";

import styles from "./SavedViews.module.scss";

const logger = getLogger("Saved Views");

type SavedViewsProps = {
    // the text we display next to "All" in the saved views dropdown
    name?: string;
    // used to determine when the "All" option should be selected
    baseParams: Partial<QueryParams>;
    className?: string;
};

// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
enum SAVE_ACTIONS {
    SAVE_AS,
    SAVE,
}

function addPageNumberToPageQuery(pageQuery: string) {
    // page queries don't store a `page` argument
    // adding it before we change the URL
    // so that any component monitoring the URL and possibly loading data
    // would load the data for page 1
    const { query, extra } = getURLQueryParams(pageQuery);
    query.page = 1;
    return [qsStringify(query), qsStringify(extra)].filter((qs) => Boolean(qs)).join("&");
}

function getSelectedViewIndex(
    savedViews: Array<SavedView>,
    searchString: string,
    lastSelectedSavedViewId: string | null,
    defaultParams: Partial<QueryParams>,
    currentViewIndex = -1
) {
    const currentParams = getURLQueryParams(searchString)?.query;
    if (isEqual(currentParams, defaultParams)) {
        return -1;
    }

    const exactMatchIndex = savedViews.findIndex((view) => areQueryParamsEqual(view.pageQuery, searchString));
    if (exactMatchIndex !== -1) {
        return exactMatchIndex;
    }

    if (lastSelectedSavedViewId) {
        // there is no exact match, but we have a reference to the last selected view
        // we assume the user changed some parameters starting from the last selected view
        // and we'll try to restore selection to that view so that they can choose
        // to overwrite the view or save the current URL as a new view
        return savedViews.findIndex((view) => view.id === lastSelectedSavedViewId);
    }

    // no views matched and we don't have a `lastSelectedSavedViewId`
    // this might happen when we selected the default view for the very first time
    // so we'll try to keep it selected
    return currentViewIndex;
}

export function SavedViews({ name, baseParams, className }: SavedViewsProps): JSX.Element {
    // `useRVDispatch` returns a `dispatch` method that is typed for our store
    const dispatch = useRVDispatch();

    const [form] = Form.useForm();

    const navigate = useNavigate();
    const location = useLocation();

    const pageName = location.pathname;
    const allText = name ? `All ${name}` : "All";

    const loading = useSelector(selectLoading);
    const savedViews = useSelector(selectSavedViews)(pageName);
    const selectedSavedViewId = useSelector(selectSelectedSavedViewId)(pageName);
    const defaultSavedView = useSelector(selectDefaultSavedView)(pageName);

    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const baseParamsWithoutPageNumber = omit(baseParams, "page");

    // dropdown
    const defaultQueryParamsString = qsStringify(baseParams); // here we want to include the page number
    const searchString = location.search?.replace(/^\?/, "") || "";

    const searchParamsWithoutPageNumber = omit(getURLQueryParams(searchString).query, "page");
    const searchStringWithoutPageNumber = qsStringify(searchParamsWithoutPageNumber);

    const [savedViewIndex, setSavedViewIndex] = useState<number>(() => {
        return getSelectedViewIndex(
            savedViews,
            searchStringWithoutPageNumber,
            selectedSavedViewId,
            baseParamsWithoutPageNumber
        );
    });
    const defaultDropdownValue =
        savedViewIndex === -1 || !savedViews[savedViewIndex]
            ? defaultQueryParamsString
            : savedViews[savedViewIndex].pageQuery;

    // save button
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const defaultSaveAction = savedViewIndex !== -1 ? SAVE_ACTIONS.SAVE : SAVE_ACTIONS.SAVE_AS;
    const saveButtonText = savedViews.length && savedViewIndex !== -1 ? "Save" : "Save As";

    // edit mode
    const [isSaveViewModalVisible, setIsSaveViewModalVisible] = useState(false);
    const [viewBeingEdited, setViewBeingEdited] = useState<SavedView>();

    const isDefaultViewEvaluated = useRef(false);

    useEffect(() => {
        if (isDataLoaded) {
            // once the data is loaded we can attempt to set a default view
            // if there is one and the current url is the page's default url
            if (
                !isDefaultViewEvaluated.current &&
                defaultSavedView &&
                (!searchString.length || areQueryParamsEqual(searchString, defaultQueryParamsString))
            ) {
                const index = savedViews.findIndex((view) => view.id === defaultSavedView.id);
                setSavedViewIndex(index);

                // page queries don't store a `page` argument
                // adding it before we change the URL
                const fullSearchString = addPageNumberToPageQuery(defaultSavedView.pageQuery);
                navigate({ search: "?" + fullSearchString }, { replace: true });
            }

            // we might have not been able to set a default view because there weren't any saved
            // or the current url is already matching another saved view
            // either way, we mark our attempt to set a default view as completed
            isDefaultViewEvaluated.current = true;
        }
    }, [savedViews, searchString, defaultQueryParamsString, defaultSavedView, navigate, isDataLoaded]);

    useEffect(() => {
        async function loadViews() {
            try {
                // unwrapping the action returned by `dispatch`
                // so that we can catch the returned promise
                await dispatch(loadSavedViews(pageName)).unwrap();
            } catch {
                message.error("Error loading views.");
            } finally {
                setIsDataLoaded(true);
            }
        }

        loadViews();
    }, [dispatch, pageName]);

    // each time the URL changes
    // update the selected saved view in the dropdown
    // and the save button
    useEffect(() => {
        const selectedViewIndex = getSelectedViewIndex(
            savedViews,
            searchStringWithoutPageNumber,
            selectedSavedViewId,
            baseParamsWithoutPageNumber,
            savedViewIndex
        );

        // we want to update the selected saved view
        setSavedViewIndex(selectedViewIndex);

        // whenever the location changes, enable/disable the save button
        const currentParams = getURLQueryParams(searchStringWithoutPageNumber)?.query;
        const selectedViewParams =
            selectedViewIndex === -1
                ? baseParamsWithoutPageNumber
                : getURLQueryParams(savedViews[selectedViewIndex]?.pageQuery)?.query;
        setIsSaveDisabled(isEqual(selectedViewParams, currentParams));
    }, [
        location.search,
        searchStringWithoutPageNumber,
        baseParamsWithoutPageNumber,
        savedViews,
        selectedSavedViewId,
        savedViewIndex,
    ]);

    // the first render of the form will happen before all the views are loaded from the backend
    // if we set the `savedViews` value to the current search string and no saved views will match the string
    //   the form will render with the current query string as the selected text, which is not what we want
    // instead we wait for the saved views to load, find a matching one
    // then reset the form, so that the `initialFormValues` variable can pre-select the correct values
    useEffect(() => {
        const prev = form.getFieldValue("savedViews");
        const current = defaultDropdownValue;

        if (prev !== current) {
            form.resetFields();
        }
    }, [defaultDropdownValue, form]);

    const onViewSelect = useCallback(
        (pageQuery: string) => {
            // persist the saved view in the redux store
            dispatch(
                setSelectedSavedView({
                    page: pageName,
                    pageQuery,
                })
            );

            // Datadog metrics
            const name = savedViews.find((v) => v.pageQuery === pageQuery)?.name || allText;
            logger.log("Saved view navigated", {
                name,
            });

            // page queries don't store a `page` argument
            // adding it before we change the URL
            const fullSearchString = addPageNumberToPageQuery(pageQuery);
            navigate({ search: "?" + fullSearchString }, { replace: true });
        },
        [navigate, dispatch, pageName, savedViews, allText]
    );

    const handleSaveAsClick = useCallback(() => {
        setIsSaveViewModalVisible(true);
        setViewBeingEdited(undefined);
    }, []);

    const onSaveViewModalCancel = useCallback(() => {
        setIsSaveViewModalVisible(false);
        setViewBeingEdited(undefined);
    }, []);

    const saveView = useCallback(
        (view: SavedView) => {
            async function upsert(view: SavedView) {
                try {
                    await dispatch(
                        // update the saved view
                        view.id
                            ? updateSavedView(view)
                            : saveNewView({
                                  page: pageName,
                                  name: view.name,
                                  pageQuery: searchStringWithoutPageNumber,
                              })
                        // unwrapping the action returned by `dispatch`
                        // so that we can catch the returned promise
                    ).unwrap();

                    message.success("View successfully saved!");
                    setIsSaveViewModalVisible(false);
                } catch {
                    message.error("Error saving view.");
                }
            }

            upsert(view);
        },
        [dispatch, pageName, searchStringWithoutPageNumber]
    );

    const deleteView = useCallback(
        async (id: string) => {
            try {
                await dispatch(deleteSavedView({ id, page: pageName })).unwrap();
                message.success("View deleted!");
            } catch {
                message.error("Error removing view.");
            }
        },
        [dispatch, pageName]
    );

    const openDeleteConfirm = useCallback(
        (savedView: SavedView) => {
            Modal.confirm({
                title: "Delete Saved View",
                icon: <ExclamationCircleOutlined />,
                content: "Are you sure you want to delete this saved view from your account?",
                okText: "Delete View",
                okButtonProps: { className: styles.dangerButton },
                onOk: () => deleteView(savedView.id),
            });
        },
        [deleteView]
    );

    const handleSaveClick = useCallback(() => {
        const savedView = savedViews[savedViewIndex];
        if (savedViewIndex === -1 || !savedView) {
            return;
        }

        // we can only save an existing view (new ones go through "Save as")
        // this means that when we directly save a view
        // we want to overwrite the `pageQuery` with the current query params
        saveView({
            ...savedView,
            pageQuery: searchStringWithoutPageNumber,
        });
    }, [savedViewIndex, savedViews, saveView, searchStringWithoutPageNumber]);

    return (
        <>
            <Form
                form={form}
                className={clsx(styles.savedViews, className)}
                initialValues={{
                    savedViews: defaultDropdownValue,
                }}
            >
                <div className={styles.dropdownContainer}>
                    <span className={styles.dropdownLabel}>Saved Views:</span>
                    {loading || !isDataLoaded ? (
                        <div data-testid={"loading-select"} className={styles.loadingView}>
                            <RVLoadingTitle size={LoadingTitleSize.SAVED_VIEW} />
                        </div>
                    ) : (
                        <RVFormItem name="savedViews" className={styles.savedViewsDropdown}>
                            <Select
                                getPopupContainer={(trigger) => trigger.parentElement}
                                loading={loading}
                                onChange={onViewSelect}
                                data-testid="saved-views-dropdown"
                                maxTagCount={"responsive"}
                            >
                                <Select.Option
                                    value={defaultQueryParamsString}
                                    title={allText}
                                    key="all"
                                    data-test-id={`saved-views-dropdown-option-all`}
                                >
                                    {allText}
                                </Select.Option>
                                {savedViews.map((savedView) => (
                                    <Select.Option
                                        value={savedView.pageQuery}
                                        className={styles.menuItem}
                                        title={savedView.name}
                                        key={savedView.id}
                                    >
                                        <span
                                            className={styles.menuItemText}
                                            data-testid={`saved-views-dropdown-option-${savedView.name}`}
                                        >
                                            {savedView.name}
                                        </span>

                                        <span className={styles.menuItemActions}>
                                            <Tooltip title="Delete View" className={styles.actionButton}>
                                                <button
                                                    type="button"
                                                    className={styles.menuItemButton}
                                                    onClick={(event: React.MouseEvent) => {
                                                        openDeleteConfirm(savedView);
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <RVIcon icon="trash" size={RVIconSizeOptions.SMALL} color="red-4" />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title="Rename View" className={styles.actionButton}>
                                                <button
                                                    type="button"
                                                    className={styles.menuItemButton}
                                                    onClick={(event: React.MouseEvent) => {
                                                        setViewBeingEdited(savedView);
                                                        setIsSaveViewModalVisible(true);

                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <RVIcon icon="edit-form" size={RVIconSizeOptions.SMALL} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    savedView.isDefault ? "Unset Default View" : "Set as Default View"
                                                }
                                                className={clsx(
                                                    styles.actionButton,
                                                    savedView.isDefault && styles.actionButtonAlwaysVisible
                                                )}
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.menuItemButton}
                                                    onClick={(event: React.MouseEvent) => {
                                                        saveView({
                                                            ...savedView,
                                                            isDefault: !savedView.isDefault,
                                                        });

                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <RVIcon
                                                        className={clsx({
                                                            [styles.favoriteIcon]: savedView.isDefault,
                                                        })}
                                                        icon={savedView.isDefault ? "star" : "star-outlined"}
                                                        size={RVIconSizeOptions.SMALL}
                                                    />
                                                </button>
                                            </Tooltip>
                                        </span>
                                    </Select.Option>
                                ))}
                            </Select>
                        </RVFormItem>
                    )}
                </div>
                {defaultSaveAction === SAVE_ACTIONS.SAVE ? (
                    <Dropdown.Button
                        className={clsx([styles.saveButton, styles.saveButtonWithDropdown])}
                        data-testid="save-button"
                        placement="bottomRight"
                        overlay={
                            <Menu>
                                <Menu.Item key="save-as-existing-view" onClick={handleSaveClick}>
                                    Save existing
                                </Menu.Item>
                                <Menu.Item key="save-as-new-view" onClick={handleSaveAsClick}>
                                    Save as new view
                                </Menu.Item>
                            </Menu>
                        }
                        getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                        icon={<RVIcon icon="arrow-down" size={RVIconSizeOptions.XSMALL} />}
                        trigger={["click"]}
                        disabled={isSaveDisabled}
                        onClick={handleSaveClick}
                    >
                        {saveButtonText}
                    </Dropdown.Button>
                ) : (
                    <Button
                        data-testid="save-button"
                        disabled={isSaveDisabled}
                        onClick={handleSaveAsClick}
                        className={styles.saveButton}
                    >
                        {saveButtonText}
                    </Button>
                )}
            </Form>
            {isSaveViewModalVisible && (
                <SaveViewModal viewBeingEdited={viewBeingEdited} onCancel={onSaveViewModalCancel} onSave={saveView} />
            )}
        </>
    );
}
